/**
 * Episode Preview/Edit Component
 *
 * Displays generated episode with inline editing capabilities:
 * - Overview: name, description, duration, complexity, tags
 * - Context: editable textarea
 * - Goals: add/remove/reorder list
 * - Personas: add/remove/reorder with name + description
 * - Scenario: editable textarea
 * - Generation Metadata: collapsible read-only section
 *
 * Story: 11.5 - Basic Episode Generator
 * Acceptance Criteria: AC 4 - Task 13
 */

import { useState, useEffect } from 'react';
import {
  type GeneratedEpisode,
  type GenerationMetadata,
  type DictionaryEntry,
} from '../services/generationApi';
import { useInitialData } from 'contexts/InitialDataContext';
import { getSkillLevelEnglishName } from 'services/initialDataApi';

// ============================================================================
// Types
// ============================================================================

interface EpisodePreviewProps {
  episode: GeneratedEpisode;
  metadata: GenerationMetadata;
  tags: Array<{ id: number; name: string }>;
  onChange: (episode: GeneratedEpisode) => void;
}

// ============================================================================
// Image Preview Component (Story 3.11)
// ============================================================================

interface ImagePreviewProps {
  url: string | null | undefined;
  alt: string;
  size?: 'small' | 'medium' | 'large';
  aspectRatio?: 'square' | 'landscape'; // Story 3.11: Support 16:9 for background images
}

/**
 * Displays an image preview with fallback for invalid/missing URLs.
 * Story 3.11: Visual Assets & Dictionary
 * - Square (1:1) for thumbnails and persona avatars
 * - Landscape for background images (natural aspect ratio)
 */
function ImagePreview({ url, alt, size = 'medium', aspectRatio = 'square' }: ImagePreviewProps) {
  const [hasError, setHasError] = useState(false);

  // Square sizes (1:1 aspect ratio) - fixed dimensions
  const squareSizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-32 h-32',
    large: 'w-48 h-48',
  };

  // Landscape max-widths (height auto for natural aspect ratio)
  const landscapeMaxWidths = {
    small: 'max-w-[160px]',
    medium: 'max-w-[280px]',
    large: 'max-w-[400px]',
  };

  // Reset error state when URL changes
  useEffect(() => {
    setHasError(false);
  }, [url]);

  if (aspectRatio === 'landscape') {
    if (!url || hasError) {
      return (
        <div
          className={`${landscapeMaxWidths[size]} w-full aspect-video bg-gray-100 border border-dashed border-gray-300 rounded-lg flex items-center justify-center`}
        >
          <div className="text-center text-gray-400 text-xs p-2">
            <svg className="w-8 h-8 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
            {hasError ? 'Load failed' : 'Pending generation'}
          </div>
        </div>
      );
    }

    return (
      <img
        src={url}
        alt={alt}
        className={`${landscapeMaxWidths[size]} w-full h-auto rounded-lg border border-gray-200`}
        onError={() => setHasError(true)}
      />
    );
  }

  // Square aspect ratio
  return (
    <div className="space-y-2">
      {url && !hasError ? (
        <img
          src={url}
          alt={alt}
          className={`${squareSizeClasses[size]} object-cover rounded-lg border border-gray-200`}
          onError={() => setHasError(true)}
        />
      ) : (
        <div
          className={`${squareSizeClasses[size]} bg-gray-100 border border-dashed border-gray-300 rounded-lg flex items-center justify-center`}
        >
          <div className="text-center text-gray-400 text-xs p-2">
            <svg className="w-8 h-8 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
            {hasError ? 'Load failed' : 'Pending generation'}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function EpisodePreview({ episode, metadata, tags, onChange }: EpisodePreviewProps) {
  const [showMetadata, setShowMetadata] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { state: initialDataState } = useInitialData();
  const skillLevels = initialDataState.data?.skillLevels || [];
  const episodeDurations = initialDataState.data?.episodeDurations || [];

  // Validate on mount and when episode changes
  useEffect(() => {
    const errors = validateEpisode(episode);
    setValidationErrors(errors);
  }, [episode]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateEpisode = (updatedEpisode: GeneratedEpisode): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Goals validation (2-3)
    if (updatedEpisode.goals.length < 2) {
      errors.goals = 'At least 2 goals are required';
    } else if (updatedEpisode.goals.length > 3) {
      errors.goals = 'Maximum 3 goals allowed';
    }

    // Personas validation (2-5, first must be Narrator)
    if (updatedEpisode.personas.length < 2) {
      errors.personas = 'At least 2 personas are required';
    } else if (updatedEpisode.personas.length > 5) {
      errors.personas = 'Maximum 5 personas allowed';
    } else if (
      (updatedEpisode.personas[0].role || updatedEpisode.personas[0].name).toLowerCase() !==
      'narrator'
    ) {
      errors.personas = 'First persona must have role "Narrator"';
    }

    return errors;
  };

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleFieldChange = (
    field: keyof GeneratedEpisode,
    value: GeneratedEpisode[keyof GeneratedEpisode]
  ) => {
    const updatedEpisode = { ...episode, [field]: value };
    const errors = validateEpisode(updatedEpisode);
    setValidationErrors(errors);
    onChange(updatedEpisode);
  };

  const handleGoalChange = (index: number, value: string) => {
    const newGoals = [...episode.goals];
    newGoals[index] = value;
    handleFieldChange('goals', newGoals);
  };

  const handleAddGoal = () => {
    if (episode.goals.length < 3) {
      handleFieldChange('goals', [...episode.goals, '']);
    }
  };

  const handleRemoveGoal = (index: number) => {
    if (episode.goals.length > 2) {
      const newGoals = episode.goals.filter((_, i) => i !== index);
      handleFieldChange('goals', newGoals);
    }
  };

  const handlePersonaChange = (
    index: number,
    field: 'name' | 'role' | 'description',
    value: string
  ) => {
    const newPersonas = [...episode.personas];
    newPersonas[index] = { ...newPersonas[index], [field]: value };
    handleFieldChange('personas', newPersonas);
  };

  const handleAddPersona = () => {
    if (episode.personas.length < 5) {
      handleFieldChange('personas', [...episode.personas, { name: '', role: '', description: '' }]);
    }
  };

  const handleRemovePersona = (index: number) => {
    if (episode.personas.length > 2 && index !== 0) {
      // Can't remove first persona (Narrator)
      const newPersonas = episode.personas.filter((_, i) => i !== index);
      handleFieldChange('personas', newPersonas);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="space-y-6" data-testid="episode-preview">
      {/* Overview Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Overview</h2>

        {/* Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Episode Name *</label>
          <input
            type="text"
            value={episode.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="name-input"
          />
        </div>

        {/* Story 3.9.15: Description field removed - episodes are now language-specific with context */}

        {/* Duration - Story 15.11: Categorical duration */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
          <select
            value={episode.durationId || ''}
            onChange={(e) => handleFieldChange('durationId', parseInt(e.target.value) || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="duration-input"
          >
            <option value="">Select duration...</option>
            {episodeDurations.map((duration) => (
              <option key={duration.id} value={duration.id}>
                {duration.localizations['en']?.name || `Duration ${duration.id}`} (≤
                {duration.max_turns} turns)
              </option>
            ))}
          </select>
        </div>

        {/* Complexity Level */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Complexity Level</label>
          <select
            value={episode.complexityLevel || ''}
            onChange={(e) => handleFieldChange('complexityLevel', Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="complexity-select"
          >
            <option value="">Select complexity level...</option>
            {skillLevels.map((skillLevel) => (
              <option key={skillLevel.id} value={skillLevel.id}>
                {getSkillLevelEnglishName(skillLevel)} ({skillLevel.id})
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
          <select
            multiple
            value={(episode.tagIds || []).map(String)}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions).map((opt) =>
                Number(opt.value)
              );
              handleFieldChange('tagIds', selectedOptions);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="tags-input"
          >
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
        </div>
      </div>

      {/* Context Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Context</h2>
        <textarea
          value={episode.context}
          onChange={(e) => handleFieldChange('context', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={5}
          data-testid="context-input"
        />
      </div>

      {/* Goals Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Goals</h2>
          <button
            type="button"
            onClick={handleAddGoal}
            disabled={episode.goals.length >= 3}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
            data-testid="add-goal-button"
          >
            + Add Goal
          </button>
        </div>

        {validationErrors.goals && (
          <p className="text-red-500 text-sm mb-2" data-testid="goals-error">
            {validationErrors.goals}
          </p>
        )}

        <div className="space-y-2">
          {episode.goals.map((goal, index) => (
            <div key={index} className="flex items-center space-x-2" data-testid={`goal-${index}`}>
              <input
                type="text"
                value={goal}
                onChange={(e) => handleGoalChange(index, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={`Goal ${index + 1}`}
                data-testid={`goal-input-${index}`}
              />
              <button
                type="button"
                onClick={() => handleRemoveGoal(index)}
                disabled={episode.goals.length <= 2}
                className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                data-testid={`remove-goal-${index}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Personas Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Personas</h2>
          <button
            type="button"
            onClick={handleAddPersona}
            disabled={episode.personas.length >= 5}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
            data-testid="add-persona-button"
          >
            + Add Persona
          </button>
        </div>

        {validationErrors.personas && (
          <p className="text-red-500 text-sm mb-2" data-testid="personas-error">
            {validationErrors.personas}
          </p>
        )}

        <div className="space-y-4">
          {episode.personas.map((persona, index) => (
            <div
              key={index}
              className="border border-gray-300 rounded-md p-4"
              data-testid={`persona-${index}`}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-700">
                  Persona {index + 1} {index === 0 && '(Narrator)'}
                </h3>
                {index !== 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePersona(index)}
                    disabled={episode.personas.length <= 2}
                    className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                    data-testid={`remove-persona-${index}`}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={persona.name}
                      onChange={(e) => handlePersonaChange(index, 'name', e.target.value)}
                      disabled={index === 0} // Can't change Narrator name
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder="e.g. Mr. Clark"
                      data-testid={`persona-name-${index}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <input
                      type="text"
                      value={persona.role || ''}
                      onChange={(e) => handlePersonaChange(index, 'role', e.target.value)}
                      disabled={index === 0} // Can't change Narrator role
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder="e.g. Landlord"
                      data-testid={`persona-role-${index}`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={persona.description}
                    onChange={(e) => handlePersonaChange(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Persona description"
                    data-testid={`persona-description-${index}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scenario Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Scenario</h2>
        <textarea
          value={episode.scenario}
          onChange={(e) => handleFieldChange('scenario', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={5}
          data-testid="scenario-input"
        />
      </div>

      {/* Story 3.11: Visual Assets Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Visual Assets</h2>
        <p className="text-sm text-gray-500 mb-4">
          AI-generated images using consistent illustrated style.
        </p>

        <div className="space-y-6">
          {/* Episode Thumbnail */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Episode Thumbnail</h3>
            <ImagePreview url={episode.thumbnailUrl} alt="Episode thumbnail" size="large" />
          </div>

          {/* Episode Background */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Episode Background</h3>
            <ImagePreview
              url={episode.backgroundImageUrl}
              alt="Episode background"
              size="large"
              aspectRatio="landscape"
            />
          </div>
        </div>

        {/* Persona Avatars */}
        <div className="mt-6">
          <h3 className="font-semibold text-gray-700 mb-3">Persona Avatars</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {episode.personas.map((persona, index) => {
              const avatarUrl =
                persona.thumbnailUrl || (index === 0 ? '/images/narrator-avatar.png' : null);
              if (!avatarUrl) return null;
              return (
                <div key={index} className="text-center">
                  <ImagePreview url={avatarUrl} alt={`${persona.name} avatar`} size="small" />
                  <p className="text-sm font-medium text-gray-700 mt-1">
                    {persona.name}
                    {index === 0 && ' (Narrator)'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Story 3.11: Dictionary Section */}
      {episode.dictionary && episode.dictionary.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">
            Dictionary ({episode.dictionary.length}{' '}
            {episode.dictionary.length === 1 ? 'entry' : 'entries'})
          </h2>
          <p className="text-sm text-gray-500 mb-4">Contextual vocabulary for this episode.</p>

          <div className="space-y-4">
            {episode.dictionary.map((entry: DictionaryEntry, index: number) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                data-testid={`dictionary-entry-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg text-blue-700">{entry.word}</h4>
                    <p className="text-gray-700 mt-1">{entry.explanation}</p>
                    {entry.examplePhrases && entry.examplePhrases.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-600">Examples:</p>
                        <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                          {entry.examplePhrases.map((phrase, pIndex) => (
                            <li key={pIndex} className="italic">
                              {phrase}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generation Metadata Section (Collapsible) */}
      <div className="bg-gray-50 rounded-lg shadow-md p-6">
        <button
          type="button"
          onClick={() => setShowMetadata(!showMetadata)}
          className="flex items-center text-lg font-medium text-gray-700 hover:text-gray-900 w-full"
          data-testid="metadata-toggle"
        >
          <span className="mr-2">{showMetadata ? '▼' : '►'}</span>
          Generation Metadata
        </button>

        {showMetadata && (
          <div className="mt-4 space-y-2" data-testid="metadata-content">
            <div>
              <span className="font-semibold">Iterations taken:</span>{' '}
              <span data-testid="metadata-iterations">{metadata.iterationsTaken} of 5</span>
            </div>

            <div>
              <span className="font-semibold">Critique decision:</span>{' '}
              <span
                className={`inline-block px-2 py-1 rounded-md text-sm font-medium ${
                  metadata.finalCritiqueDecision === 'ACCEPT'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
                data-testid="metadata-decision"
              >
                {metadata.finalCritiqueDecision}
              </span>
            </div>

            {metadata.critiqueFeedback.length > 0 && (
              <div>
                <span className="font-semibold">Critique feedback:</span>
                <ul className="list-disc list-inside mt-1" data-testid="metadata-feedback">
                  {metadata.critiqueFeedback.map((feedback, index) => (
                    <li key={index} className="text-sm text-gray-600">
                      {feedback}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
